import { EncodingResult } from '@mode-vegas/vegas';
import * as angular from 'angular';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, first, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { tokenFromVizID } from '@mode/charts/domain';
import { DataLimitsResults, toChartDataError } from '@mode/charts/ui';
import { getVegasChartDataErrorMsg } from '@mode/charts/util-muze';
import { ChartFormatHelpers, makeSummaryData, RenderDataModel } from '@mode/charts/util-vegas';
import {
  EmbedActions,
  EmbedActionType,
  EmbedReportActions,
  EmbedReportState,
  ReportStore,
} from '@mode/reports/sandbox/contract';
import { ChartTypes, InterActions } from '@mode/shared/contract-common';
import { errorGif } from '@mode/shared/ui-images';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import {
  generateUUID,
  shareReplayDefault,
  debounceResize,
  getAppName,
  isBuilderMode,
  isReportMode,
} from '@mode/shared/util-js';
import { AnalyticsEvent, AnalyticsProps, AnalyticsService } from '@mode/shared/util-segment';
import { MixableBaseController } from '../../../common/component';
import { ChartExportHelper } from '../../../lib/chart-export-helper';
import { getChartDataErrorMsg } from '../../../lib/charts/chart-data';
import { ReportApiService } from '../../../services/report-api-service';
import ReportLinkService from '../../../services/report-link-service';
import { EmbedChartSelectors, EmbedReportSelectors, FlamingoSelectors } from '../domain/embed-report.selectors';

class VegasChartController extends MixableBaseController {
  static $inject = [
    '$timeout',
    '$element',
    '$window',
    'ReportApiService',
    'ReportLinkService',
    'RxStoreService',
    'AnalyticsService',
    'FeatureFlagsFacade',
  ];

  onTitleChanged: (args: { $vizID: string; $title: string }) => void;
  onCaptionInfoChanged: (args: { $vizID: string; $captionInfo: ChartTypes.CaptionInfo }) => void;
  onAction: (args: { $action: EmbedActions }) => void;
  onRenderEnd: (args: { $visualizationRendered: boolean; $type: string; $options: any }) => void;
  onRenderStart: (args: { $vizId: string; $type: string }) => void;

  readonly copyText = `Copy to ${getAppName()} clipboard`;
  private domCache: angular.IAugmentedJQuery | null = null;

  chartId: string;
  token: string;
  queryToken: string;
  renderInfo: Readonly<ChartTypes.VegasRenderInfo>;
  chartTitle: string;
  reportLevelColorMap: { [seriesName: string]: number };
  dataset: EmbedReportState.EmbedDataset;
  inViewport: boolean;

  queryToken$: Observable<string>;
  isPurged$: Observable<boolean>;
  isValid$: Observable<boolean>;
  planFailed$: Observable<boolean>;
  loading$: Observable<boolean>;
  showRenderer$: Observable<boolean>;
  renderModel$: Observable<RenderDataModel>;
  canUndo$: Observable<boolean>;
  canRedo$: Observable<boolean>;
  canHierarchyBack$: Observable<boolean>;
  canHierarchyForward$: Observable<boolean>;
  hierarchyEnabled$: Observable<boolean>;
  planError$: Observable<string>;
  planErrorHelp$: Observable<string>;
  noResults$: Observable<boolean>;
  private shownNotifier = new BehaviorSubject<boolean>(false);
  private shown$: Observable<boolean>;
  unaggregatedExportEnabled$: Observable<boolean>;
  wleCsvExportEnabled$: Observable<boolean>;

  focusLink: string;
  viewer: string;
  isEmbedded: boolean;
  isMetricChart: boolean;
  drilldownEnabled = false;
  drillAnywhereEnabled = false;
  exploreEnabled = false;
  contextMenuSubscription: Subscription;
  canRender = true; // Whether the chart meets the requirements for rendering
  disableDomCache = false;

  encodingErrors: string[];
  errorImg: string;
  renderError: string;
  renderErrorHelp: string;

  isDesignerMode?: boolean;

  showTitle = true;
  chartCaptionText: string;
  showCaptionText = true;
  captionLocation = ChartTypes.ChartCaptionPosition.Top;

  constructor(
    private $timeout: angular.ITimeoutService,
    private $element: angular.IAugmentedJQuery,
    private $window: angular.IWindowService,
    private reportApiService: ReportApiService,
    private reportLinkService: ReportLinkService,
    private rxStore: ReportStore,
    private analyticsService: AnalyticsService,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    super();
    this.errorImg = errorGif;

    this.shown$ = this.shownNotifier.pipe(
      filter((shown) => shown),
      first() // Don't want to continue firing after being shown once
    );

    this.unaggregatedExportEnabled$ = featureFlagsFacade.asObservable(FeatureFlag.UnaggregatedChartExport);
    this.wleCsvExportEnabled$ = featureFlagsFacade.asObservable(FeatureFlag.WleCsvExport);
  }

  $onInit() {
    this.token = this.chartId.split('_')[1];

    this.queryToken$ = this.rxStore
      .select(EmbedChartSelectors.createQueryTokenSelector(this.token))
      .pipe(shareReplayDefault(), takeUntil(this.destroyed$));

    this.queryToken$
      .pipe(switchMap((queryToken) => this.rxStore.select(EmbedReportSelectors.createDatasetSelector(queryToken))))
      .subscribe((dataset) => {
        this.dataset = dataset;
      });

    this.queryToken$.subscribe((queryToken) => {
      this.queryToken = queryToken;
    });

    this.contextMenuSubscription = this.reportApiService.subscribe(this.chartId, (action) => {
      if (action.type === InterActions.Type.ContextClick) {
        switch (action.item.type) {
          case InterActions.ContextItemType.ViewData:
            this.onAction({
              $action: {
                type: EmbedActionType.ViewData,
                target: action.item.target,
                queryToken: this.queryToken,
                chartToken: this.chartId.replace('chart_', ''),
              },
            });
            break;
        }
      }
    });

    this.isPurged$ = this.rxStore
      .select(EmbedChartSelectors.createDatasetResultSelector(this.queryToken))
      .pipe(map((datasetResult) => datasetResult.isPurged));

    this.isValid$ = this.shown$.pipe(
      switchMap(() =>
        this.rxStore.select(FlamingoSelectors.createIsValidSelector(this.token)).pipe(shareReplayDefault())
      )
    );

    this.renderModel$ = this.rxStore
      .select(EmbedChartSelectors.createRenderDataModelSelector(this.token))
      .pipe(shareReplayDefault());
    const flamingoError$ = this.rxStore.select(FlamingoSelectors.createFriendlyErrorSelector(this.token));
    this.planFailed$ = this.rxStore.select(FlamingoSelectors.createPlanFailedSelector(this.token));
    this.planError$ = flamingoError$.pipe(map((err) => err.msg));
    this.planErrorHelp$ = flamingoError$.pipe(map((err) => err.helpText));
    this.loading$ = this.shown$.pipe(
      switchMap(() => this.rxStore.select(FlamingoSelectors.createPlanLoadingSelector(this.token))),
      startWith(true), // Start with true since shown$ can take time to fire
      shareReplayDefault()
    );

    this.noResults$ = this.renderModel$.pipe(map((rm) => rm == null || rm.isEmpty()));

    this.showRenderer$ = combineLatest([
      this.isValid$,
      this.rxStore.select(FlamingoSelectors.createPlanSuccessSelector(this.token)),
    ]).pipe(map(([valid, success]) => valid && success));

    this.focusLink = this.renderInfo._vizLink;
    const historyState$ = this.rxStore.select(EmbedChartSelectors.createEmbedStateSelector(this.token));
    this.canUndo$ = historyState$.pipe(map((history) => history.canUndo));
    this.canRedo$ = historyState$.pipe(map((history) => history.canRedo));

    const hierarchy$ = this.rxStore
      .select(EmbedChartSelectors.createHierarchySelector(this.token))
      .pipe(shareReplayDefault());
    this.canHierarchyBack$ = hierarchy$.pipe(map((h) => h.back));
    this.canHierarchyForward$ = hierarchy$.pipe(map((h) => h.forward));
    this.hierarchyEnabled$ = hierarchy$.pipe(map((h) => (h.back || h.forward) && !this.isMetricChart));

    this.loading$
      .pipe(
        takeUntil(this.destroyed$),
        filter((loading) => loading)
      )
      .subscribe(() => {
        this.domCache = null;
        this.renderStart();
      });

    // If we're in an error state, fire render end to get rid of the placeholder
    combineLatest([this.isValid$, this.planFailed$, this.loading$])
      .pipe(
        takeUntil(this.destroyed$),
        filter(([isValid, failed, loading]) => (!isValid && !loading) || failed)
      )
      .subscribe(([isValid, failed]) => {
        this.canRender = isValid && !failed;
        this.renderEnd(null);
      });
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.renderInfo && this.renderInfo != null) {
      this.reportLevelColorMap = this.renderInfo.reportLevelColorMap;
      this.chartTitle = this.renderInfo.title;
      this.chartCaptionText = this.renderInfo.caption.text;
      this.showCaptionText = !this.renderInfo.caption.isHidden;
      this.showTitle = !this.renderInfo.caption.isTitleHidden;
      this.captionLocation =
        this.renderInfo.caption.position === ChartTypes.ChartCaptionPosition.Bottom
          ? ChartTypes.ChartCaptionPosition.Bottom
          : ChartTypes.ChartCaptionPosition.Top;
    }

    if (changes.inViewport && this.inViewport) {
      this.shownNotifier.next(true);
    }

    if (changes.inViewport && !this.disableDomCache && this.domCache != null && this.domCache.length > 0) {
      if (this.inViewport) {
        if (this.domCache[0].hasAttribute('style')) {
          this.domCache[0].removeAttribute('style');
          this.renderEnd(null);
        }
      } else {
        this.domCache[0].setAttribute('style', 'display: none');
      }
    } else if (changes.inViewport && !this.canRender) {
      // Remove placeholder for errored charts when back in viewport
      if (this.inViewport) {
        this.renderEnd(null);
      }
    }
  }

  $onDestroy() {
    if (this.contextMenuSubscription) {
      this.contextMenuSubscription.unsubscribe();
    }
  }

  isLinkable() {
    return this.reportLinkService.isLinkable();
  }

  linkToData($event) {
    if (this.isLinkable()) {
      $event.preventDefault();
      $event.stopPropagation();
      this.reportLinkService.linkToPage(this.queryToken, 'data');
    }
  }

  export(title: string) {
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.ReportView,
      action: AnalyticsEvent.Interaction.DownloadCsv,
      resource: AnalyticsEvent.Context.Report,
      chartType: this.renderInfo.chartType,
    });

    this.renderModel$.pipe(first(), takeUntil(this.destroyed$)).subscribe((rm) => {
      const chartData = makeSummaryData(rm, this.renderInfo.encoding);
      if (chartData) ChartExportHelper.saveAs(ChartExportHelper.makeCsv(chartData), title);
    });
  }

  linkToQuery($event) {
    if (this.isLinkable()) {
      $event.preventDefault();
      $event.stopPropagation();
      this.reportLinkService.linkToPage(this.queryToken, 'query');
    }
  }

  linkToLightbox($event) {
    this.noResults$.pipe(first()).subscribe((noResults) => {
      if (!noResults && this.isLinkable()) {
        const analyticsProps = this.getAnalyticsProps();

        $event.preventDefault();
        $event.stopPropagation();

        this.onAction({
          $action: {
            type: EmbedActionType.ExploreChart,
            chartToken: tokenFromVizID(this.chartId),
            props: analyticsProps,
          },
        });
      }
    });
  }

  captureKebabClick() {
    const analyticsProps = this.getAnalyticsProps();

    this.onAction({
      $action: {
        type: EmbedActionType.ExpandKebab,
        chartToken: tokenFromVizID(this.chartId),
        props: analyticsProps,
      },
    });
  }

  copySuccess() {
    this.onAction({
      $action: {
        type: EmbedActionType.ShowAlert,
        success: true,
        message: 'Link to chart copied successfully',
      },
    });
  }

  copyChart() {
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.ReportView,
      action: 'Copy Chart',
      resource: AnalyticsEvent.Context.Report,
      id: this.token,
    });
    this.onAction({
      $action: {
        type: EmbedActionType.CopyChart,
        token: this.token,
      },
    });
  }

  updateTitle($title: string) {
    this.chartTitle = $title;
    this.onTitleChanged({ $vizID: this.chartId, $title });
  }

  updateCaptionText($captionText: string) {
    this.chartCaptionText = $captionText;
    this.onCaptionInfoChanged({
      $vizID: this.chartId,
      $captionInfo: { ...this.renderInfo.caption, text: $captionText },
    });
  }

  isReportMode(): boolean {
    return isReportMode(this.$window.document.body);
  }

  isBuilderMode(): boolean {
    return isBuilderMode(this.$window.document.body);
  }

  get reportContext() {
    return this.isBuilderMode() ? 'report_builder' : 'report_viewer';
  }

  useMuzeRenderer(): boolean {
    return ChartFormatHelpers.useMuzeRenderer(this.renderInfo.chartType, this.renderInfo.format);
  }

  titleEditDisabled(): boolean {
    return this.isReportMode();
  }

  updateEncoding(encResult: EncodingResult) {
    this.onAction({
      $action: {
        type: EmbedActionType.UpdateEncoding,
        chartToken: this.chartId.replace('chart_', ''),
        encResult,
      },
    });
  }

  dispatchEmbedAction(action: EmbedActions) {
    this.onAction({ $action: action });
  }

  dispatchAction($event: { componentId: string; action: InterActions.Union }) {
    this.reportApiService.dispatch($event.componentId, $event.action);
  }

  changeHierarchy(forward: boolean) {
    const analyticsProps = this.getAnalyticsProps();

    this.onAction({
      $action: {
        type: EmbedActionType.ChangeHierarchy,
        chartToken: this.chartId.replace('chart_', ''),
        forward: forward,
        props: analyticsProps,
      },
    });
  }

  getAnalyticsProps(): AnalyticsProps {
    return {
      event_id: generateUUID(),
      product_context: AnalyticsEvent.Context.Report,
      sub_context: AnalyticsEvent.SubContext.Chart,
      chart_id: this.chartId,
      chart_type: this.renderInfo.chartType,
    };
  }

  undo(ev: Event) {
    ev.preventDefault();
    this.onAction({
      $action: {
        type: EmbedActionType.Back,
        chartToken: this.chartId.replace('chart_', ''),
      },
    });
  }

  redo(ev: Event) {
    ev.preventDefault();
    this.onAction({
      $action: {
        type: EmbedActionType.Forward,
        chartToken: this.chartId.replace('chart_', ''),
      },
    });
  }

  renderStart() {
    this.renderError = null;
    this.renderErrorHelp = null;
    this.onRenderStart({ $vizId: this.chartId, $type: this.renderInfo.chartType });
  }

  renderEnd(err: Error) {
    const errorMsgs = err !== null && err !== undefined ? getChartDataErrorMsg(err) : undefined;
    this.handleRenderEnd(errorMsgs);
  }

  renderEndVegas($event: { error?: Error; options?: any }) {
    const error = $event?.error;
    const options = $event?.options;
    const errorMsgs = error !== null && error !== undefined ? getVegasChartDataErrorMsg(error) : undefined;
    this.handleRenderEnd(errorMsgs, options);
  }

  handleRenderEnd(error: { renderError: string; renderErrorHelp: string } | undefined, options?: any) {
    this.renderError = undefined;
    this.renderErrorHelp = undefined;
    if (error !== null && error !== undefined) {
      const { renderError, renderErrorHelp } = error;
      this.renderError = renderError;
      this.renderErrorHelp = renderErrorHelp;
    }

    this.domCache = this.$element.find('chart-renderer-too,mode-viz-renderer');
    const visualizationRendered = (error === null || error === undefined) && this.canRender;
    this.onRenderEnd({
      $visualizationRendered: visualizationRendered,
      $type: this.renderInfo.chartType,
      $options: options,
    });
  }

  dataLimitsExceeded(results: DataLimitsResults) {
    // Treat this as an error
    const error = toChartDataError(results);
    this.renderEnd(error);
  }

  stopEventPropagation($event) {
    $event.stopPropagation();
  }

  onHeaderSizeChange() {
    debounceResize(this.$window);
  }

  viewUnaggregatedData() {
    this.rxStore.dispatch(new EmbedReportActions.ViewUnaggregatedDataAction(this.token, this.queryToken));
  }
}

export const VegasChartComponent: angular.IComponentOptions = {
  bindings: {
    chartId: '@id',
    renderInfo: '<',
    viewer: '<',
    isEmbedded: '<',
    isMetricChart: '<',
    status: '<',
    inViewport: '<',
    drilldownEnabled: '<',
    drillAnywhereEnabled: '<',
    exploreEnabled: '<',
    isDesignerMode: '<',
    disableDomCache: '<',
    onTitleChanged: '&',
    onCaptionInfoChanged: '&',
    onRenderStart: '&',
    onRenderEnd: '&',
    onAction: '&',
  },
  controllerAs: 'ctrl',
  controller: VegasChartController,
  template: `
    <div class="editor-module editor-chart mode-object editor-multi">
      <div class="chart-content">
        <div class="chart-header caption-chart-header">
          <mode-in-place-input
            ng-if="ctrl.showTitle"
            placeholder="Untitled chart"
            [readonly]="ctrl.isReportMode()"
            [text]="ctrl.chartTitle"
            [maxchars]="250"
            (text-change)="ctrl.updateTitle($event)"
            ng-dblclick="ctrl.stopEventPropagation($event)"
          ></mode-in-place-input>
          <!-- New report actions start -->
          <div class="new-viz-actions" ng-if="(ctrl.isValid$ | async:this)">
            <button
              class="new-viz-action action-left hide-2 plus-plan-only"
              ng-click="ctrl.undo($event)"
              ng-class="{ disabled: !(ctrl.canUndo$ | async:this)}">
              <svg-icon type="'back'" uib-tooltip="Undo"></svg-icon>
            </button>

            <button
              class="new-viz-action action-right hide-2 plus-plan-only"
              ng-click="ctrl.redo($event)"
              ng-class="{ disabled: !(ctrl.canRedo$ | async:this)}">
              <svg-icon type="'forward'" uib-tooltip="Redo" ng-if="ctrl.viewer !== 'whiteLabel'"></svg-icon>
              <!--
                Workaround to place tooltip on the left on WLE so it doesn't conflict with scrollbar when it's the last element in a row.
                Ideally, this would be better handled with a WLE CSS class that would only turn this on when it's the last
                item in a row.
              -->
              <svg-icon type="'forward'" uib-tooltip="Redo" ng-if="ctrl.viewer === 'whiteLabel'" tooltip-placement="left"></svg-icon>
            </button>

            <div class="new-viz-actions__section" ng-if="ctrl.viewer !== 'whiteLabel'">
              <copy-link-to-viz link="ctrl.focusLink" ng-if="!ctrl.isMetricChart">
                <button
                  class="new-viz-action hide-3"
                  uib-tooltip="Get Share Link"
                  copy-to-clipboard
                  clipboard-text="ctrl.focusLink"
                  clipboard-success="ctrl.copySuccess()"
                  clipboard-enabled="isClipboardEnabled">
                  <svg-icon type="'copy-link'"></svg-icon>
                </button>
              </copy-link-to-viz>
              <button class="new-viz-action kebab-action underlying-data-menu"
                ng-click="ctrl.captureKebabClick($event)">
                <kebab-menu targetable="true">
                  <li menu-item="exp" class="show-2" ng-click="ctrl.undo()" disabled="!(ctrl.canUndo$ | async:this)">Undo</li>
                  <li menu-item="exp" class="show-2" ng-click="ctrl.redo()" disabled="!(ctrl.canRedo$ | async:this)">Redo</li>
                  <li menu-item="exp" class="show-2 divider"></li>
                  <li menu-item="exp" role="button" tabindex="0" ng-keyup="$event.keyCode == 13 && ctrl.export(ctrl.chartTitle)" ng-click="ctrl.export(ctrl.chartTitle)" data-test="export-csv-link">Download chart data to CSV</li>
                  <li menu-item="exp" class="mode-hide-embed" ng-if="ctrl.unaggregatedExportEnabled$ | async:this" ng-click="ctrl.viewUnaggregatedData()">View underlying chart data </li>
                  <li menu-item="exp" class="divider"></li>
                  <li menu-item="exp" ng-if="!ctrl.viewer" ng-click="ctrl.copyChart()">{{ ctrl.copyText }}</li>
                  <li menu-item="exp" class="divider"></li>
                  <li menu-item="exp" ng-click="ctrl.linkToData($event)" ng-href="{{ ctrl.dataset.reportDataUrl }}">View raw data <span class="mode-show-embed-inline">in Mode</span></li>
                  <li menu-item="exp" ng-click="ctrl.linkToQuery($event)" ng-href="{{ ctrl.dataset.reportQueryUrl }}" ng-if="!ctrl.viewer">View source <span class="mode-show-embed-inline">in Mode</span></li>
                </kebab-menu>
              </button>
            </div>

            <div class="new-viz-actions__section" ng-if="ctrl.viewer === 'whiteLabel' && (ctrl.wleCsvExportEnabled$ | async:this)">
              <button class="new-viz-action kebab-action underlying-data-menu"
                ng-click="ctrl.captureKebabClick($event)">
                <kebab-menu targetable="true">
                  <li menu-item="exp" role="button" tabindex="0" ng-keyup="$event.keyCode == 13 && ctrl.export(ctrl.chartTitle)" ng-click="ctrl.export(ctrl.chartTitle)" data-test="export-csv-link">Download chart data to CSV</li>
                </kebab-menu>
              </button>
            </div>

            <div class="new-viz-actions__section" ng-if="!ctrl.viewer">
              <span class="divider"></span>

              <a
                class="viz-explore-action btn btn-default"
                ng-class="{ disabled: (ctrl.noResults$ | async:this) }"
                ng-if="ctrl.exploreEnabled && !ctrl.isMetricChart"
                ng-href="{{ ctrl.renderInfo._vizReportRunLink }}"
                ng-click="ctrl.linkToLightbox($event)">
                Explore
              </a>

              <a class="new-viz-action"
                ng-if="!ctrl.exploreEnabled && !ctrl.isMetricChart"
                ng-href="{{ ctrl.renderInfo._vizReportRunLink }}"
                ng-click="ctrl.linkToLightbox($event)" class="new-viz-action">
                <svg-icon class="mode-hide-embed" type="'toggle-expanded-view'" uib-tooltip="Expand"></svg-icon>
                <svg-icon class="mode-show-embed-flex-inline" type="'toggle-expanded-view'" uib-tooltip="Expand in Mode" tooltip-placement="left"></svg-icon>
              </a>

              <span class="new-viz-action no-border no-action"
                ng-if="ctrl.isMetricChart">
                <svg-icon class="mode-hide-embed" type="'metric-badge'" tooltip-class="tooltip-pre-wrap" tooltip-placement="left" uib-tooltip="This chart is powered by dbt Metrics"></svg-icon>
                <svg-icon class="mode-show-embed-flex-inline" type="'metric-badge'" uib-tooltip="This chart is powered by dbt Metrics" tooltip-placement="left"></svg-icon>
              </span>
            </div>
          </div>
          <!-- New report actions end -->
        </div>
        <div class="chart">
          <div class="chart-svg"
          ng-class="{ 'with-no-title': !ctrl.showTitle,
                      'with-granularity': (ctrl.drilldownEnabled || (ctrl.renderInfo.chartType === 'vegas')) && (ctrl.hierarchyEnabled$ | async:this) }">
            <div class="caption-container"
              ng-if="ctrl.showCaptionText"
              ng-class="{ 'caption-position-bottom': (ctrl.captionLocation === 'bottom' )}">
              <mode-in-place-textarea
                class="chart-caption"
                placeholder="Click to add chart description. Shift-enter for new line."
                [maxchars]="360"
                [text]="ctrl.chartCaptionText"
                [readonly]="ctrl.isReportMode()"
                (text-change)="ctrl.updateCaptionText($event)"
                (size-change)="ctrl.onHeaderSizeChange()"
                ng-mousedown="ctrl.stopEventPropagation($event)"
                ng-dblclick="ctrl.stopEventPropagation($event)">
              </mode-in-place-textarea>
            </div>
            <mode-viz-renderer
              ng-if="(ctrl.showRenderer$ | async:this) && !ctrl.renderError && (ctrl.isValid$ | async:this) && ctrl.useMuzeRenderer() && (ctrl.inViewport || ctrl.domCache != null)"
              [token]="ctrl.token"
              [render-model]="ctrl.renderModel$ | async:this"
              [render-context]="ctrl.reportContext"
              [encoding]="ctrl.renderInfo.encoding"
              [format]="ctrl.renderInfo.format"
              [field-formats]="ctrl.renderInfo.fieldFormats"
              [field-colors]="ctrl.renderInfo.fieldColors"
              [report-level-color-map]="ctrl.reportLevelColorMap"
              [facet-sizes]="ctrl.renderInfo.facetSizes"
              [drilldown-enabled]="ctrl.drilldownEnabled"
              [drill-anywhere-enabled]="ctrl.drillAnywhereEnabled"
              [is-temporal-drill-allowed]="ctrl.hierarchyEnabled$ | async:this"
              [is-designer-mode]="ctrl.isDesignerMode"
              [is-white-label]="ctrl.viewer && ctrl.viewer === 'whiteLabel'"
              [is-embedded]="ctrl.isEmbedded"
              [report-api-service]="ctrl.reportApiService"
              [chart-type]="ctrl.renderInfo.chartType"
              (render-start)="ctrl.renderStart()"
              (render-end)="ctrl.renderEndVegas($event)"
              (data-limits-exceeded)="ctrl.dataLimitsExceeded($event)"
              (chart-action)="ctrl.dispatchAction($event)"
              (embed-action)="ctrl.dispatchEmbedAction($event)">
            </mode-viz-renderer>
            <chart-renderer-too
              token="ctrl.token"
              ng-if="(ctrl.showRenderer$ | async:this) && !ctrl.renderError && (ctrl.isValid$ | async:this) && !ctrl.useMuzeRenderer() && (ctrl.inViewport || ctrl.domCache != null)"
              render-model="ctrl.renderModel$ | async:this"
              chart-type="ctrl.renderInfo.chartType"
              render-context="report"
              encoding="ctrl.renderInfo.encoding"
              format="ctrl.renderInfo.format"
              report-level-color-map="ctrl.reportLevelColorMap"
              drilldown-enabled="ctrl.drilldownEnabled"
              is-designer-mode="ctrl.isDesignerMode"
              is-white-label="ctrl.viewer && ctrl.viewer === 'whiteLabel'"
              is-guest="ctrl.viewer && ctrl.viewer === 'guest'"
              is-embedded="ctrl.isEmbedded"
              render-start="ctrl.renderStart()"
              render-end="ctrl.renderEnd($err)"
              on-embed-action="ctrl.dispatchEmbedAction($action)"
            ></chart-renderer-too>
            <div class="drill-actions"
            ng-if="ctrl.drilldownEnabled && (ctrl.hierarchyEnabled$ | async:this)"
            ng-class="{ 'viz-explorer-drill-actions': (ctrl.renderInfo.chartType === 'vegas')}">
              <button class="drill-action-btn"
                ng-class="{ disabled: !(ctrl.canHierarchyBack$ | async:this) }"
                ng-click="ctrl.changeHierarchy(false)"
                ng-dblclick="$event.stopPropagation()">
                <svg-icon
                  class="icon-hierarchy-up"
                  type="'hierarchy-up'"
                  uib-tooltip="Decrease Granularity"
                  tooltip-append-to-body="true"
                  tooltip-placement="top"
                  tooltip-class="hierarchy-tooltip">
                </svg-icon>
              </button>
              <button class="drill-action-btn"
                ng-class="{ disabled: !(ctrl.canHierarchyForward$ | async:this)}"
                ng-click="ctrl.changeHierarchy(true)"
                ng-dblclick="$event.stopPropagation()">
                <svg-icon
                  class="icon-hierarchy-down"
                  type="'hierarchy-down'"
                  uib-tooltip="Increase Granularity"
                  tooltip-append-to-body="true"
                  tooltip-placement="top"
                  tooltip-class="hierarchy-tooltip">
                </svg-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="chart-error" ng-show="ctrl.planFailed$ | async:this">
        <h3 ng-bind="ctrl.planError$ | async:this"></h3>
        <p ng-bind="ctrl.planErrorHelp$ | async:this"></p>
      </div>
      <div ng-if="(ctrl.isPurged$ | async:this)" class="chart-error">
        <h3>No results</h3>
      </div>
      <div ng-if="(ctrl.isValid$ | async:this) === false && !(ctrl.isPurged$ | async:this)" class="chart-error">
        <h3>Sorry, the chart configuration is invalid or incomplete</h3>
        <p class="sub-message">Try editing the chart to check the configuration</p>
      </div>
      <div class="chart-error" ng-if="ctrl.renderError"><h3 ng-bind="ctrl.renderError" /><p ng-bind="ctrl.renderErrorHelp" /></div>
    </div>`,
};
